/**
 * ScrapeKing Design System v2 - Page Header Component
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC } from 'react';
import styled, { keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const typewriter = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const HeaderWrapper = styled.div`
  margin-bottom: var(--sk-space-8);
  animation: ${slideIn} 0.4s ease;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--sk-space-6);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: var(--sk-space-2);
  margin-bottom: var(--sk-space-3);
  animation: ${fadeIn} 0.3s ease 0.1s backwards;
`;

const BreadcrumbItem = styled.span`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xs);
  color: var(--sk-text-tertiary);

  a {
    color: var(--sk-text-tertiary);
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: var(--sk-cyan-500);
    }
  }
`;

const BreadcrumbSeparator = styled.span`
  color: var(--sk-surface-border);
  font-family: var(--sk-font-mono);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-4);
`;

const StatusBadge = styled.span<{ $status?: 'active' | 'warning' | 'error' | 'idle' }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-2);
  padding: var(--sk-space-1) var(--sk-space-3);
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  border-radius: var(--sk-radius-full);
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => {
    switch (props.$status) {
      case 'active':
        return `
          background: rgba(0, 255, 127, 0.1);
          color: var(--sk-green-500);
          border: 1px solid var(--sk-green-700);
        `;
      case 'warning':
        return `
          background: rgba(255, 184, 0, 0.1);
          color: var(--sk-amber-500);
          border: 1px solid var(--sk-amber-700);
        `;
      case 'error':
        return `
          background: rgba(255, 0, 51, 0.1);
          color: var(--sk-red-500);
          border: 1px solid var(--sk-red-700);
        `;
      default:
        return `
          background: var(--sk-surface-default);
          color: var(--sk-text-secondary);
          border: 1px solid var(--sk-surface-border);
        `;
    }
  }}

  /* Status dot */
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const Title = styled.h1`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-3xl);
  font-weight: 700;
  color: var(--sk-text-primary);
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
  position: relative;

  /* Subtle gradient text effect */
  background: linear-gradient(
    135deg,
    var(--sk-text-primary) 0%,
    var(--sk-text-primary) 70%,
    var(--sk-cyan-400) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: var(--sk-text-2xl);
  }
`;

const Subtitle = styled.p`
  font-size: var(--sk-text-base);
  color: var(--sk-text-secondary);
  margin: var(--sk-space-2) 0 0;
  max-width: 600px;
  line-height: 1.6;
  animation: ${fadeIn} 0.4s ease 0.2s backwards;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  flex-shrink: 0;
  animation: ${fadeIn} 0.4s ease 0.3s backwards;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    var(--sk-surface-border) 0%,
    var(--sk-cyan-700) 50%,
    var(--sk-surface-border) 100%
  );
  margin-top: var(--sk-space-6);
  opacity: 0.5;
`;

// Terminal-style command indicator
const CommandIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-2);
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xs);
  color: var(--sk-text-tertiary);
  margin-top: var(--sk-space-4);
  padding: var(--sk-space-2) var(--sk-space-3);
  background: var(--sk-bg-secondary);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-sm);

  span.prompt {
    color: var(--sk-cyan-500);
  }

  span.command {
    color: var(--sk-green-400);
  }
`;

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

interface BreadcrumbItemType {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItemType[];
  status?: 'active' | 'warning' | 'error' | 'idle';
  statusText?: string;
  actions?: React.ReactNode;
  showDivider?: boolean;
  commandHint?: string;
}

export const PageHeader: FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  status,
  statusText,
  actions,
  showDivider = true,
  commandHint,
}) => {
  return (
    <HeaderWrapper>
      <HeaderTop>
        <HeaderContent>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumb>
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={index}>
                  <BreadcrumbItem>
                    {item.href ? <a href={item.href}>{item.label}</a> : item.label}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && (
                    <BreadcrumbSeparator>/</BreadcrumbSeparator>
                  )}
                </React.Fragment>
              ))}
            </Breadcrumb>
          )}

          <TitleRow>
            <Title>{title}</Title>
            {status && statusText && (
              <StatusBadge $status={status}>{statusText}</StatusBadge>
            )}
          </TitleRow>

          {subtitle && <Subtitle>{subtitle}</Subtitle>}

          {commandHint && (
            <CommandIndicator>
              <span className="prompt">$</span>
              <span className="command">{commandHint}</span>
            </CommandIndicator>
          )}
        </HeaderContent>

        {actions && <HeaderActions>{actions}</HeaderActions>}
      </HeaderTop>

      {showDivider && <Divider />}
    </HeaderWrapper>
  );
};

// ═══════════════════════════════════════════════════════════════
// SECTION HEADER (smaller, for content sections)
// ═══════════════════════════════════════════════════════════════

const SectionWrapper = styled.div`
  margin-bottom: var(--sk-space-5);
`;

const SectionTitle = styled.h2`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-lg);
  font-weight: 600;
  color: var(--sk-text-primary);
  margin: 0;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
`;

const SectionBadge = styled.span`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  color: var(--sk-cyan-500);
  background: rgba(0, 200, 224, 0.1);
  padding: var(--sk-space-1) var(--sk-space-2);
  border-radius: var(--sk-radius-sm);
  border: 1px solid var(--sk-cyan-800);
`;

const SectionDescription = styled.p`
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
  margin: var(--sk-space-2) 0 0;
`;

interface SectionHeaderProps {
  title: string;
  description?: string;
  badge?: string;
  actions?: React.ReactNode;
}

export const SectionHeader: FC<SectionHeaderProps> = ({
  title,
  description,
  badge,
  actions,
}) => {
  return (
    <SectionWrapper>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SectionTitle>
          {title}
          {badge && <SectionBadge>{badge}</SectionBadge>}
        </SectionTitle>
        {actions}
      </div>
      {description && <SectionDescription>{description}</SectionDescription>}
    </SectionWrapper>
  );
};

export default PageHeader;
